import { useTranslate } from '@refinedev/core';
import { Avatar, Card, Flex, Grid, Tabs, Typography, theme } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import type { ActivityDetailOut } from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { StatCell } from '@/components/common/show/StatCell';
import { orDash } from '@/components/common/show/shared';
import { ExecutionSourceTag } from '@/components/system/tags';
import { ActivitySubject } from '@/components/system/ActivitySubject';
import { formatDateTime } from '@/utils/format';
import { ACTIVITY_SHOW_TABS } from '../constants';
import type { ActivityShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<ActivityShowTab, string> = {
  general: 'base.sections.basic_info',
};

interface ActivityShowHeaderProps {
  record: ActivityDetailOut;
  activeTab: ActivityShowTab;
  onTabChange: (key: string) => void;
}

export const ActivityShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: ActivityShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const f = (key: string) => t(`system.activities.fields.${key}`);

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <Avatar
          size={64}
          icon={<HistoryOutlined />}
          style={{ backgroundColor: token.colorPrimary }}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long verbs wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                fontFamily: 'monospace',
                overflowWrap: 'anywhere',
              }}
            >
              {record.verb ?? '—'}
            </Title>
            {record.source && <ExecutionSourceTag source={record.source} />}
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('base.fields.created_at')}>
              {orDash(formatDateTime(record.created_at))}
            </StatCell>
            <StatCell label={f('actor')}>
              {orDash(
                record.actor_id != null ? (
                  <ShowIconLink resource="iam.users" id={record.actor_id}>
                    {record.actor_email ?? record.actor_id}
                  </ShowIconLink>
                ) : (
                  t('system.activities.empty_actor')
                )
              )}
            </StatCell>
            <StatCell label={f('object')}>
              <ActivitySubject
                type={record.object_type}
                id={record.object_id}
              />
            </StatCell>
            <StatCell label={f('target')}>
              <ActivitySubject
                type={record.target_type}
                id={record.target_id}
              />
            </StatCell>
            <StatCell label={f('transaction_id')}>
              {record.transaction_id}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={ACTIVITY_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
